import React, { useState, useEffect } from "react"
import { get, put } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"
import { sortData } from "../../utils/common"
import moment from "moment-timezone"
import { ChevronUp, ChevronDown } from "lucide-react"

const History = () => {
  let [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [roleToChange, setRoleToChange] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filterRole, setFilterRole] = useState("ALL")
  const [searchText, setSearchText] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [sortConfig, setSortConfig] = useState({
    key: "eventTimestamp",
    direction: "desc",
  })

  // Search Filter
  // const filteredHistory = history?.filter((data) => {
  //   return Object.entries(data).some(([key, value]) => {
  //     // For other fields, apply standard filtering
  //     return value?.toString().toLowerCase().includes(searchText.toLowerCase())
  //   })
  // })

  const filteredHistory = history?.filter((data) => {
    const matchesSearch = Object.entries(data).some(([key, value]) => {
      // Handle special formatting for eventTimestamp
      if (key === "eventTimestamp") {
        const formattedDate = moment
          .utc(Number(value) * 1000)
          .tz("America/New_York")
          .format("MM/DD/YYYY hh:mm A")
        return formattedDate
          .toLowerCase()
          .includes(searchText.trim().toLowerCase())
      }

      // Regular value search
      return value
        ?.toString()
        .toLowerCase()
        .includes(searchText.trim().toLowerCase())
    })

    const matchesRole = filterRole === "ALL" || data.loggedInRole === filterRole

    return matchesSearch && matchesRole
  })

  const sortedFaxes = sortData(filteredHistory, sortConfig)
  const startIdx = (currentPage - 1) * rowsPerPage
  const currentHistory = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(sortedFaxes.length / rowsPerPage)

  useEffect(() => {
    loadAppConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAppConfigs = async () => {
    try {
      const data = await get("/admin/settings-audits")

      setHistory(data)
      setCurrentPage(1) // reset to first page
    } catch (error) {
      console.error("Failed to fetch app configs", error)
    }
  }

  const loadAppConfigs = async () => {
    setLoading(true)
    await fetchAppConfigs()
    setLoading(false)
  }

  const handleRoleChange = async (role) => {
    const { emailAddress, prevRole, newRole } = role
    try {
      setHistory((prev) =>
        prev.map((config) =>
          config.emailAddress === emailAddress
            ? { ...config, userRole: newRole }
            : config,
        ),
      )

      await put("/admin/updateRole", { emailAddress, newRole, prevRole })

      toast.success("Role updated successfully")
    } catch (error) {
      console.error("Failed to update role", error)
      toast.error(error)
      setHistory((prev) =>
        prev.map((config) =>
          config.emailAddress === emailAddress
            ? { ...config, userRole: prevRole }
            : config,
        ),
      )
    }
  }

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", width: "100%", overflow: "hidden", boxSizing: "border-box" }}
        >
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <div className="setting-note">
              <select
                className="select-dropdown"
                style={{ width: "200px" }}
                value={filterRole}
                onChange={(e) => {
                  setFilterRole(e.target.value)
                }}
              >
                <option value="ALL">All</option>
                <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </div>
            <div style={{ flexShrink: 0 }}>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={`Search ...`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                  style={{ width: "200px", paddingRight: "22px" }}
                />
                <button
                  type="button"
                  onClick={() => { setSearchText(""); setCurrentPage(1) }}
                  className="search-clear-btn"
                  style={{ visibility: searchText ? "visible" : "hidden" }}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th
                    className="faxes-th"
                    style={{ textAlign: "center", width: "250px" }}
                  >
                    Performed By
                  </th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Role
                  </th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Action
                  </th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Module
                  </th>
                  <th className="faxes-th" style={{ textAlign: "center" }}>
                    Summary
                  </th>
                  <th
                    className="faxes-th"
                    style={{
                      textAlign: "center",
                      width: "150px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      handleSort("eventTimestamp")
                    }}
                  >
                    Event Time
                    <span>
                      {sortConfig.direction === "asc" ? <ChevronUp size={14} style={{ verticalAlign: "middle" }} /> : <ChevronDown size={14} style={{ verticalAlign: "middle" }} />}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentHistory.length > 0 ? (
                  currentHistory.map((config) => (
                    <tr
                      key={config.eventTimestamp}
                      className={`fax-item${hoveredRow === config.eventTimestamp ? " row-highlighted" : ""}`}
                      style={{ cursor: "unset" }}
                      onMouseEnter={() => setHoveredRow(config.eventTimestamp)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.loggedInUser}
                      </td>
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.loggedInRole}
                      </td>
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.eventType}
                      </td>
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.eventModule}
                      </td>
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {config.eventSummary}
                      </td>
                      <td className="faxes-td" style={{ boxShadow: "none" }}>
                        {moment
                          .utc(Number(config.eventTimestamp) * 1000)
                          .tz("America/New_York")
                          .format("MM/DD/YYYY hh:mm A")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredHistory.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredHistory.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredHistory.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {roleToChange && (
        <ConfirmationPopup
          onConfirm={() => {
            handleRoleChange(roleToChange)
            setRoleToChange(null)
          }}
          onCancel={() => setRoleToChange(null)}
          roleToChange={roleToChange}
        />
      )}
    </div>
  )
}

const ConfirmationPopup = ({ onConfirm, onCancel, roleToChange }) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px" }}>
      <p
        style={{ marginTop: "0px" }}
      >{`Are you sure you want to change the role of ${roleToChange.emailAddress} to ${roleToChange.newRole}?`}</p>
      <div className="popup-buttons">
        <button className="cancel" onClick={onCancel}>
          No
        </button>
        <button className="save" onClick={onConfirm}>
          Yes
        </button>
      </div>
    </div>
  </div>
)

export default History
